import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Check, Loader2, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import FlowSteps from "@/components/FlowSteps";
import { makeApi } from "@/lib/api";
import ReferralCodeBox from "@/components/ReferralCodeBox";
import RenewalCard from "@/components/RenewalCard";
import { Checkbox } from "@/components/ui/checkbox";

const periodLabel = (pkg, autoRenew) => {
  if (!pkg.interval) return autoRenew ? (pkg.lookup_key === "cl_whiteglove_once" ? "RENEWS YEARLY" : "RENEWS EVERY 2 MONTHS") : "ONE-TIME";
  if (pkg.interval === "year") return "PER YEAR";
  return pkg.interval_count > 1 ? `EVERY ${pkg.interval_count} MONTHS` : "PER MONTH";
};

export default function Pricing() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null);
  const [referral, setReferral] = useState(null);
  const [autoRenew, setAutoRenew] = useState(true);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPackages(data.packages || []));
  }, []);

  const applies = (k) => !!referral && (referral.kind === "thanks" || referral.plans.includes(k));

  const checkout = async (lookup_key) => {
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        quantity: 1,
        auto_renew: autoRenew,
        origin_url: window.location.origin,
        ...(applies(lookup_key) ? { referral_code: referral.code } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(e.response?.status === 403 ? "Checkout is turned off in preview mode." : typeof detail === "string" ? detail : "Could not start checkout. Try again.");
      setLoading(null);
    }
  };

  const navigate = useNavigate();
  const { t } = useLang();
  const goBack = () => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate("/compliance"));

  return (
    <Shell>
      <FlowSteps current={2} />
      <button onClick={goBack} data-testid="pricing-back-button"
        className="mb-6 min-h-[44px] px-4 py-2 border border-[#262626] hover:border-white font-mono-label text-xs inline-flex items-center gap-2 transition-colors">
        <ChevronLeft size={16} /> {t("back")}
      </button>
      <div className="mb-10">
        <div className="font-mono-label text-xs text-[#FFD600] mb-2">PRICING</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl leading-none" data-testid="pricing-title">Pick a Plan.</h1>
        <p className="text-muted-foreground mt-3 max-w-xl">All plans include the full guided wizard. Upgrade when you want expert review or a done-for-you launch.</p>
      </div>

      <RenewalCard api={api} />
      <ReferralCodeBox api={api} onChange={setReferral} />

      <label className="mb-6 flex items-start gap-3 max-w-xl cursor-pointer" data-testid="auto-renew-option">
        <Checkbox checked={autoRenew} onCheckedChange={(v) => setAutoRenew(!!v)} data-testid="auto-renew-checkbox"
          className="mt-0.5 border-[#FFD600] data-[state=checked]:bg-[#FFD600] data-[state=checked]:text-black" />
        <span className="text-sm">
          <span className="font-semibold">Auto-renew my plan</span>
          <span className="block text-muted-foreground text-xs mt-0.5">
            Your card is charged again when the plan period ends (one-time plans renew every 2 months or yearly). We email you 3 days before. Turn it off anytime on this page.
          </span>
        </span>
      </label>

      <div className="grid md:grid-cols-3 gap-0 brutal-border" data-testid="pricing-grid">
        {packages.map((pkg) => (
          <div key={pkg.lookup_key} data-testid={`plan-${pkg.lookup_key}`} className="p-8 border-r border-b border-[#262626] bg-[#0A0A0A] flex flex-col">
            <div className="font-mono-label text-[10px] text-[#FFD600] mb-2">{pkg.lookup_key.toUpperCase().replace("CL_", "")}</div>
            <div className="font-heading font-black text-2xl mb-1">{pkg.product_name}</div>
            <div className="text-sm text-muted-foreground mb-6 min-h-[48px]">{pkg.description}</div>
            {applies(pkg.lookup_key) ? (
              <div className="mb-1" data-testid={`discounted-${pkg.lookup_key}`}>
                <span className="font-heading font-black text-5xl">${(pkg.amount * (1 - referral.pct / 100)).toFixed(2).replace(/\.00$/, "")}</span>
                <span className="ml-2 text-lg text-muted-foreground line-through">${pkg.amount}</span>
                <div className="font-mono-label text-[10px] text-[#4ADE80] mt-1">{referral.pct}% {referral.kind === "thanks" ? "THANK-YOU DISCOUNT · FIRST PAYMENT" : "REFERRAL DISCOUNT"}</div>
              </div>
            ) : (
              <div className="font-heading font-black text-5xl mb-1">${pkg.amount}</div>
            )}
            <div className="font-mono-label text-[10px] text-muted-foreground mb-6">
              {periodLabel(pkg, autoRenew)}{pkg.referral_eligible && !referral ? " · REFERRAL CODES WORK HERE" : ""}
            </div>
            <ul className="space-y-2 mb-8 flex-1">
              {(pkg.perks || []).map((p, i) => (
                <li key={i} className="text-sm flex items-start gap-2"><Check size={14} className="text-[#FFD600] mt-1 shrink-0" />{p}</li>
              ))}
            </ul>
            <button
              onClick={() => checkout(pkg.lookup_key)}
              disabled={loading === pkg.lookup_key}
              data-testid={`checkout-${pkg.lookup_key}`}
              className="cta-primary px-5 py-3 font-mono-label text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading === pkg.lookup_key ? <Loader2 size={14} className="animate-spin" /> : null}
              {loading === pkg.lookup_key ? "Starting…" : "Get this plan"}
            </button>
          </div>
        ))}
        {packages.length === 0 && (
          <div className="p-8 col-span-3 text-muted-foreground text-sm">Loading plans…</div>
        )}
      </div>
    </Shell>
  );
}
